// Foursquare Places Service
// Free external business search when BookAPT database doesn't have results
// 100,000 FREE searches per month!

import axios from 'axios';
import { Platform } from 'react-native';
import { geocodingService } from './geocodingService';

// Import API key from config (or use placeholder if not configured)
let FOURSQUARE_API_KEY = 'fsq3YOUR_API_KEY_HERE'; // Default placeholder

try {
  // Try to import from config file
  const foursquareConfig = require('../config/foursquareConfig');
  if (foursquareConfig?.FOURSQUARE_API_KEY && foursquareConfig.FOURSQUARE_API_KEY !== 'fsq3YOUR_API_KEY_HERE') {
    FOURSQUARE_API_KEY = foursquareConfig.FOURSQUARE_API_KEY;
    console.log('✅ Foursquare API key loaded from config');
  } else {
    console.warn('⚠️ Foursquare API key not configured. See src/config/foursquareConfig.ts');
  }
} catch (error) {
  // Config file doesn't exist yet - that's okay, will use placeholder
  console.log('ℹ️ Foursquare config not found - using placeholder. See FOURSQUARE_SETUP_GUIDE.md');
}

export interface ExternalBusiness {
  id: string;
  name: string;
  category: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  phone?: string;
  website?: string;
  rating?: number;
  distance?: number; // in meters
  photos?: string[];
  isOnBookAPT: boolean;
  source: 'foursquare' | 'bookapt';
  ownerId?: string; // Business owner's user ID (for BookAPT businesses)
}

class FoursquarePlacesService {
  // Updated to use new Foursquare Places API endpoints
  // Migration guide: https://docs.foursquare.com/fsq-developers-places/reference/migration-guide
  // New API: places-api.foursquare.com/places (no /v3/ version in path)
  // Use proxy in browser to avoid CORS issues
  private getBaseUrl(): string {
    // Mobile (React Native) first: no window.location, use backend proxy only
    if (Platform.OS !== 'web') {
      const backendUrl = 'https://bookapt-payment-api.onrender.com';
      return `${backendUrl}/api/foursquare/places`;
    }

    // Web: use window.location only when it exists (undefined in RN even when window exists)
    if (typeof window !== 'undefined' && window.location) {
      const hostname = window.location.hostname || '';
      const isProduction = hostname !== 'localhost' &&
                          !hostname.includes('127.0.0.1') &&
                          !hostname.includes('192.168.');
      if (isProduction) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL ||
                          process.env.REACT_APP_API_URL ||
                          'https://bookapt-payment-api.onrender.com';
        return `${backendUrl}/api/foursquare/places`;
      }
      return '/api/foursquare/places';
    }

    // Node.js/server: direct API
    return 'https://places-api.foursquare.com/places';
  }
  
  // Check if we're using the backend proxy (production)
  private isUsingBackendProxy(): boolean {
    if (Platform.OS !== 'web') {
      return true;
    }
    if (typeof window !== 'undefined' && window.location) {
      const hostname = window.location.hostname || '';
      return hostname !== 'localhost' &&
             !hostname.includes('127.0.0.1') &&
             !hostname.includes('192.168.');
    }
    return false;
  }
  
  private apiKey = FOURSQUARE_API_KEY;
  
  // Get base URL dynamically (use method instead of property)
  private get baseUrl(): string {
    return this.getBaseUrl();
  }

  /**
   * Search for businesses near a location
   */
  async searchNearby(params: {
    query: string;
    zipcode: string;
    category?: string;
    limit?: number;
    radius?: number; // in meters
  }): Promise<ExternalBusiness[]> {
    try {
      // Convert zipcode to lat/lng using geocoding service
      // Supports: US zipcodes (5-digit) and Canadian postal codes (A1A 1A1)
      const location = await geocodingService.zipcodeToLatLng(params.zipcode);
      
      if (!location) {
        console.warn('Could not geocode zipcode:', params.zipcode);
        return [];
      }

      // Build search parameters
      // Note: Foursquare API has migrated - check migration guide for parameter changes
      // Migration guide: https://docs.foursquare.com/fsq-developers-places/reference/migration-guide
      // Version is required as 'v' query parameter (YYYYMMDD format)
      const radiusMeters = params.radius || 5000; // Default 5km (5000 meters)
      const searchParams = new URLSearchParams({
        ll: `${location.lat},${location.lng}`, // Location as lat,lng
        radius: radiusMeters.toString(), // Use provided radius or default 5km
        limit: (params.limit || 50).toString(), // Increased default to 50 (Foursquare API max is typically 50)
        // Note: New API uses header for versioning, not query parameter
        // Note: intent parameter not supported in new API
      });

      // Map BookAPT category IDs to Foursquare category IDs
      // Using numeric category IDs from official Foursquare Places API
      // Reference: Complete category list provided by user
      const categoryMap: { [key: string]: string } = {
          // Beauty & Wellness - Health and Beauty Service (11061) and subcategories
          'beauty_wellness': '11061,11062,11063,11064,11065,11067,11070,11071,11072,11073,11074,11075',
          // 11061: Business and Professional Services > Health and Beauty Service (parent)
          // 11062: Barbershop, 11064: Hair Salon, 11071: Nail Salon, 11073: Spa, etc.
          'hair': '11064', // Hair Salon
          'spa': '11073', // Spa
          'nails': '11071', // Nail Salon
          'beauty': '11061', // Health and Beauty Service (parent)
          
          // Automotive - Business and Professional Services > Automotive Service (11009) + Retail > Automotive Retail (17005)
          'automotive': '11009,11010,11011,11012,11013,11014,11015,11016,11017,11018,17005,17006,17007,17008,17009,17010,17011,17012,17013,17140,17141',
          // 11009: Automotive Service (parent)
          // 11010-11018: All automotive service subcategories (Repair Shop, Car Wash, Oil Change, Tire Repair, Towing, etc.)
          // 17005: Automotive Retail (parent)
          // 17006-17013, 17140-17141: All automotive retail subcategories (Car Dealership, Parts, Motorcycle, etc.)
          'auto': '11009,11010,11011,11012,11013,11014,11015,11016,11017,11018,17005,17006,17007,17008,17009,17010,17011,17012,17013,17140,17141', // Auto Service (alias)
          
          // Health & Fitness - Gym and Studio (18021) and subcategories
          'health_fitness': '18021,18022,18023,18024,18025,18026,18027,18028,18038,18077',
          // 18021: Gym and Studio (parent)
          // 18022: Boxing Gym, 18023: Climbing Gym, 18024: Cycle Studio, 18027: Pilates Studio, 18028: Yoga Studio, 18038: Personal Trainer, 18077: Gym
          'fitness': '18021,18077', // Gym and Studio
          
          // Food & Dining - Restaurant (13065) and all restaurant subcategories
          'food_dining': '13065', // Restaurant (parent - includes all restaurant types)
          'restaurant': '13065', // Restaurant (parent)
          
          // Medical & Dental - Health and Medicine categories
          'medical_dental': '15007,15012,15014,15015,15016,15017,15026,15027,15031',
          // 15007: Dentist, 15012: Home Health Care Service, 15014: Hospital, 15015: Maternity Clinic
          // 15016: Medical Center, 15017: Medical Lab, 15026: Physical Therapy Clinic, 15027: Physician, 15031: Doctor's Office
          
          // Home & Garden - Home Improvement Service (11076) and subcategories
          'home_garden': '11076,11077,11078,11079,11080,11081,11082,11083,11084,11085,11086,11087,11088,11089,11090,11091,11092,11093,11094,11095,11096,11097,11098,11099,11100,11101,11102',
          // 11076: Home Improvement Service (parent)
          // Includes: Carpenter, Electrician, Plumber, Landscaper, Painter, Pest Control, Professional Cleaning Service, etc.
          
          // Pet Care - Pet Service (11133) and subcategories
          'pet_care': '11133,11134,11135,15054',
          // 11133: Pet Service (parent), 11134: Pet Grooming Service, 11135: Pet Sitting and Boarding Service
          // 15054: Veterinarian
          
          // Technology - Technology Business (11167) and subcategories
          'technology': '11167,11168,11169,11027,11108',
          // 11167: Technology Business (parent), 11168: Software Company, 11169: Website Designer
          // 11027: Computer Repair Service, 11108: IT Service
          
          // Legal Services
          'legal': '11111,11112,11113,11114',
          // 11111: Legal Service (parent), 11112: Law Office, 11113: Immigration Attorney, 11114: Notary
          
          // Financial Services
          'financial': '11042,11043,11045,11053,11055',
          // 11042: Financial Service (parent), 11043: Accounting and Bookkeeping Service, 11045: Bank, 11053: Financial Planner, 11055: Stock Broker
          
          // Real Estate
          'real_estate': '11145,11146,11147,11148,11149,11150,11151',
          // 11145: Real Estate Service (parent), 11146: Building and Land Surveyor, 11147: Commercial Real Estate Developer
          // 11148: Property Management Office, 11149: Real Estate Agency, 11150: Real Estate Appraiser, 11151: Real Estate Development and Title Company
          
          // Education & Tutoring
          'education': '11172,12009,12010,12011,12013,12049,12050,12052,12053,12054',
          // 11172: Tutoring Service, 12009: Education (parent), 12010: Adult Education, 12011: Art School, 12013: College and University
          // 12049: Computer Training School, 12050: Culinary School, 12052: Flight School, 12053: Language School, 12054: Music School
          
          // Event Planning
          'event_planning': '11038,11039,11040',
          // 11038: Event Service, 11039: Event Space, 11040: Factory (sometimes used for events)
          
          // Photography
          'photography': '11137,11138,11139,11140',
          // 11137: Photography Service (parent), 11138: Photographer, 11139: Photography Lab, 11140: Photography Studio
          
          // Construction
          'construction': '11028',
          // 11028: Construction
          
          // Moving & Storage
          'moving_storage': '11093,11165',
          // 11093: Mover, 11165: Storage Facility
          
          // Transportation
          'transportation': '19051,19052,19053,19067,19068',
          // 19051: Transportation Service (parent), 19052: Charter Bus, 19053: Limo Service, 19067: Bus Line, 19068: Taxi
          
          // Childcare
          'childcare': '11025,11026',
          // 11025: Child Care Service (parent), 11026: Daycare
          
          // Security & Safety
          'security_safety': '11162,11115',
          // 11162: Security and Safety (parent), 11115: Locksmith
          
          // Digital Marketing
          'digital_marketing': '11001,11121',
          // 11001: Advertising Agency, 11121: Media Agency
          
          // Senior Care
          'senior_care': '15004,12095,12096',
          // 15004: Assisted Living Service, 12095: Retirement Home, 12096: Senior Citizen Service
          
          // Sports Coaching
          'sports_coaching': '18038,18003,18007,18014,18017,18023,18032,18043,18046,18049,18052,18063,18065,18074',
          // 18038: Personal Trainer, plus various sports clubs and facilities
          
          // Personal Styling
          'personal_styling': '17040,17041,17042,17043,17044,17045,17050,17052',
          // Fashion Retail categories: Clothing Store, Bridal Store, Fashion Accessories Store, Jewelry Store, Men's Store, Women's Store
        };
      
      // Add categories if provided (do this BEFORE query to prioritize category filter)
      // New API may use different category parameter format
      if (params.category && params.category !== 'all') {
        const categoryId = categoryMap[params.category.toLowerCase()];
        if (categoryId) {
          // Foursquare Places API - try both parameter names
          // Old API used 'categoryId' (singular), new API might use 'categories' (plural)
          // Try 'categories' first (new API format), but also try 'categoryId' if that doesn't work
          const categoryIds = categoryId.split(',').map(id => id.trim());
          
          // Use first category ID for categoryId parameter (old format)
          // Use all category IDs for categories parameter (new format)
          searchParams.append('categories', categoryId); // New API format (plural)
          // Also add categoryId with first ID (old API format - might still work)
          if (categoryIds.length > 0) {
            searchParams.append('categoryId', categoryIds[0]);
          }
          
        } else {
          console.warn('⚠️ Unknown category:', params.category, '- searching without category filter');
        }
      }
      
      // Add query if provided
      // For category filtering, we'll use query as a fallback if category filter doesn't work
      // Map categories to search queries as backup
      const categoryQueryMap: { [key: string]: string } = {
        'automotive': 'auto repair car service',
        'auto': 'auto repair car service',
        'hair': 'hair salon',
        'spa': 'spa massage',
        'fitness': 'gym fitness',
        'restaurant': 'restaurant',
        'beauty': 'beauty salon',
        'beauty_wellness': 'hair salon beauty spa', // Better query for beauty & wellness
      };
      
      // Normalize query for better Foursquare results
      let normalizedQuery = params.query || '';
      if (normalizedQuery) {
        const queryLower = normalizedQuery.toLowerCase();
        // Map common beauty/wellness search terms to better Foursquare queries
        if (queryLower.includes('haircut') || queryLower.includes('hair cut')) {
          normalizedQuery = 'hair salon';
        } else if (queryLower.includes('hair') && !queryLower.includes('salon')) {
          normalizedQuery = 'hair salon';
        } else if (queryLower.includes('nail')) {
          normalizedQuery = 'nail salon';
        } else if (queryLower.includes('spa') && !queryLower.includes('massage')) {
          normalizedQuery = 'spa';
        } else if (queryLower.includes('beauty') && !queryLower.includes('salon')) {
          normalizedQuery = 'beauty salon';
        }
      }
      
      if (normalizedQuery && normalizedQuery.toLowerCase() !== 'all' && normalizedQuery.trim() !== '') {
        // Use query parameter - it helps Foursquare find relevant businesses
        // Use query even with category filter - they work together
        searchParams.append('query', normalizedQuery);
      } else if (params.category && params.category !== 'all') {
        // If no query provided but category is set, use category-specific query as backup
        const fallbackQuery = categoryQueryMap[params.category.toLowerCase()];
        if (fallbackQuery) {
          searchParams.append('query', fallbackQuery);
        }
      }
      
      // Try new API endpoint format
      // Foursquare has migrated to new endpoints - old /v3/places/search is deprecated
      // Trying new endpoint structure based on migration guide
      const endpoint = '/search';
      const requestUrl = `${this.baseUrl}${endpoint}?${searchParams.toString()}`;

      // Make API call with new format
      let response;
      const maxRetries = 2;
      let lastError: any = null;
      
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          if (attempt > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          }
          
          // Try new endpoint format - may need different base URL
          // First try: current base URL with updated parameters
          // When using backend proxy, don't send Authorization header (backend handles it)
          const headers: any = {
            'Accept': 'application/json',
          };
          
          if (this.isUsingBackendProxy()) {
            // Backend proxy handles authentication - don't send API key from frontend
            // Backend proxy handles auth
          } else {
            // Direct API call or webpack proxy - send Authorization header
            headers['Authorization'] = `Bearer ${this.apiKey}`; // New auth format: Bearer <SERVICE_KEY>
            headers['X-Places-Api-Version'] = '2025-06-17'; // Version header: YYYY-MM-DD format (required)
          }
          
          response = await axios.get(requestUrl, {
            headers,
            timeout: 30000,
          });
          
          break; // Success!
          
        } catch (error: any) {
          lastError = error;
          
          // If 410 (deprecated), the endpoint structure has changed
          if (error.response?.status === 410) {
            console.error('❌ Foursquare API endpoint returned 410 (Gone) - endpoint deprecated');
            console.error('💡 Foursquare has migrated to new API endpoints');
            console.error('📖 Migration guide: https://docs.foursquare.com/fsq-developers-places/reference/migration-guide');
            console.error('💡 Current baseUrl:', this.baseUrl);
            console.error('💡 You need to check the migration guide and update the baseUrl in foursquarePlacesService.ts');
            
            if (error.response?.data?.message) {
              console.error('Error details:', error.response.data.message);
            }
            
            // Return empty array - user needs to check migration guide and update code
            // The endpoint format has changed and we need the correct format from Foursquare
            return [];
          } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            if (attempt < maxRetries) {
              console.warn(`⏱️ Request timeout (attempt ${attempt + 1}), will retry...`);
              continue;
            }
            throw error;
          } else {
            throw error;
          }
        }
      }
      
      if (!response) {
        throw lastError || new Error('Failed to get response from Foursquare API');
      }

      // Parse results - v3 API response structure may be different
      // Try both response.data.results and response.data (direct array)
      let results = [];
      if (response.data?.results) {
        results = response.data.results;
      } else if (Array.isArray(response.data)) {
        results = response.data;
      } else if (response.data?.data) {
        results = response.data.data;
      }
      
      const businesses = this.parseResults(results);
      return businesses;

    } catch (error: any) {
      // Handle different error status codes
      if (error.response?.status === 401) {
        console.error('❌ Foursquare API authentication failed (401).');
        console.error('💡 The new Places API requires a Service Key (not an old API key).');
        console.error('📖 Get a Service Key at: https://developer.foursquare.com/');
        console.error('📝 See: FOURSQUARE_SERVICE_KEY_SETUP.md for instructions');
        if (error.response?.data) {
          console.error('Error details:', JSON.stringify(error.response.data));
        }
        // Return empty array instead of mock data
        return [];
      }
      
      if (error.response?.status === 410) {
        console.error('❌ Foursquare API endpoint returned 410 (Gone).');
        console.error('💡 The endpoint /v3/places/search or /v3/places/nearby may have been deprecated.');
        console.error('💡 Foursquare may have changed their API structure. Check the latest docs.');
        console.error('📖 Check Foursquare API docs: https://docs.foursquare.com/developer/reference/places-search');
        if (error.response?.data) {
          console.error('Error details:', JSON.stringify(error.response.data));
        }
        // Return empty array instead of mock data
        return [];
      }
      
      if (this.apiKey === 'fsq3YOUR_API_KEY_HERE') {
        console.warn('⚠️ Foursquare API key not configured. Businesses will not be shown.');
        console.warn('📖 Setup guide: FOURSQUARE_SETUP_GUIDE.md');
        // Return empty array instead of mock data
        return [];
      }
      
      // Handle timeout errors specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.error('❌ Foursquare API request timed out after 30 seconds.');
        console.error('💡 Possible causes:');
        console.error('   - Slow network connection');
        console.error('   - Foursquare API is slow or down');
        console.error('   - CORS issues (if running in browser)');
        console.error('💡 Solution: Check network connection or try again later.');
        // Return empty array instead of mock data
        return [];
      }
      
      // Handle network errors
      if (error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        console.error('❌ Network error connecting to Foursquare API:', error.message);
        console.error('💡 Check your internet connection.');
        // Return empty array instead of mock data
        return [];
      }
      
      console.error('Error searching Foursquare:', error.message);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', JSON.stringify(error.response.data, null, 2));
        if (error.response.data?.message) {
          console.error('Error message:', error.response.data.message);
        }
        if (error.response.data?.errors) {
          console.error('API errors:', error.response.data.errors);
        }
      } else if (error.request) {
        console.error('No response received from Foursquare API');
        console.error('Request URL:', error.config?.url);
      }
      // Return empty array instead of mock data
      return [];
    }
  }

  /**
   * Parse Foursquare API response into our format
   */
  private parseResults(results: any[]): ExternalBusiness[] {
    return results.map((place: any, index: number) => {
      const location = place.location || {};
      const category = place.categories?.[0]?.name || 'Other';
      
      // New API uses fsq_place_id, old API used fsq_id
      // Fallback to index if neither exists (shouldn't happen)
      const placeId = place.fsq_place_id || place.fsq_id || `unknown_${index}`;
      
      return {
        id: `fsq_${placeId}`,
        name: place.name,
        category: category,
        address: location.address || '',
        city: location.locality || '',
        state: location.region || '',
        zipcode: location.postcode || '',
        phone: place.tel,
        website: place.website,
        rating: place.rating ? place.rating / 2 : undefined, // Foursquare uses 0-10, convert to 0-5
        distance: place.distance,
        photos: place.photos?.map((p: any) => `${p.prefix}original${p.suffix}`),
        isOnBookAPT: false,
        source: 'foursquare',
      };
    });
  }


  /**
   * Mock results for development/testing (when API key not configured)
   */
  private getMockResults(query: string, zipcode: string): ExternalBusiness[] {
    console.log('📦 Using mock Foursquare data (configure API key for real data)');

    const queryLower = query.toLowerCase();
    
    // Return different mock data based on query
    if (queryLower.includes('hair') || queryLower.includes('salon') || queryLower.includes('barber')) {
      return [
        {
          id: 'fsq_mock_1',
          name: 'Elite Hair Studio',
          category: 'Hair Salon',
          address: '123 Main St',
          city: 'Arlington',
          state: 'TX',
          zipcode: zipcode,
          phone: '(555) 123-4567',
          rating: 4.5,
          distance: 400,
          isOnBookAPT: false,
          source: 'foursquare',
        },
        {
          id: 'fsq_mock_2',
          name: 'Modern Cuts',
          category: 'Barber Shop',
          address: '456 Oak Ave',
          city: 'Arlington',
          state: 'TX',
          zipcode: zipcode,
          phone: '(555) 234-5678',
          rating: 4.3,
          distance: 700,
          isOnBookAPT: false,
          source: 'foursquare',
        },
        {
          id: 'fsq_mock_3',
          name: 'Bella Beauty Salon',
          category: 'Hair Salon',
          address: '789 Elm St',
          city: 'Arlington',
          state: 'TX',
          zipcode: zipcode,
          phone: '(555) 345-6789',
          rating: 4.7,
          distance: 1200,
          isOnBookAPT: false,
          source: 'foursquare',
        },
      ];
    }

    if (queryLower.includes('oil') || queryLower.includes('auto') || queryLower.includes('car') || queryLower.includes('mechanic')) {
      return [
        {
          id: 'fsq_mock_4',
          name: "Joe's Auto Shop",
          category: 'Auto Repair',
          address: '321 Commerce St',
          city: 'Arlington',
          state: 'TX',
          zipcode: zipcode,
          phone: '(555) 456-7890',
          rating: 4.6,
          distance: 500,
          isOnBookAPT: false,
          source: 'foursquare',
        },
        {
          id: 'fsq_mock_5',
          name: 'AutoCare Plus',
          category: 'Auto Service',
          address: '654 Industrial Blvd',
          city: 'Arlington',
          state: 'TX',
          zipcode: zipcode,
          phone: '(555) 567-8901',
          rating: 4.4,
          distance: 800,
          isOnBookAPT: false,
          source: 'foursquare',
        },
        {
          id: 'fsq_mock_6',
          name: 'Quick Lube & Tire',
          category: 'Oil Change',
          address: '987 Highway 360',
          city: 'Arlington',
          state: 'TX',
          zipcode: zipcode,
          phone: '(555) 678-9012',
          rating: 4.2,
          distance: 1500,
          isOnBookAPT: false,
          source: 'foursquare',
        },
      ];
    }

    if (queryLower.includes('restaurant') || queryLower.includes('food') || queryLower.includes('dining')) {
      return [
        {
          id: 'fsq_mock_7',
          name: "Luigi's Italian Kitchen",
          category: 'Italian Restaurant',
          address: '111 Restaurant Row',
          city: 'Arlington',
          state: 'TX',
          zipcode: zipcode,
          phone: '(555) 789-0123',
          rating: 4.8,
          distance: 300,
          isOnBookAPT: false,
          source: 'foursquare',
        },
        {
          id: 'fsq_mock_8',
          name: 'The Steakhouse',
          category: 'Steakhouse',
          address: '222 Downtown Ave',
          city: 'Arlington',
          state: 'TX',
          zipcode: zipcode,
          phone: '(555) 890-1234',
          rating: 4.6,
          distance: 600,
          isOnBookAPT: false,
          source: 'foursquare',
        },
      ];
    }

    // Default generic results
    return [
      {
        id: 'fsq_mock_generic_1',
        name: `${query} Service Center`,
        category: 'Service',
        address: '100 Main St',
        city: 'Arlington',
        state: 'TX',
        zipcode: zipcode,
        phone: '(555) 000-0001',
        rating: 4.0,
        distance: 1000,
        isOnBookAPT: false,
        source: 'foursquare',
      },
      {
        id: 'fsq_mock_generic_2',
        name: `Professional ${query}`,
        category: 'Service',
        address: '200 Oak Ave',
        city: 'Arlington',
        state: 'TX',
        zipcode: zipcode,
        phone: '(555) 000-0002',
        rating: 4.2,
        distance: 1500,
        isOnBookAPT: false,
        source: 'foursquare',
      },
    ];
  }

  /**
   * Get detailed information about a specific place
   */
  async getPlaceDetails(placeId: string): Promise<ExternalBusiness | null> {
    try {
      // Remove 'fsq_' prefix if present
      const fsqId = placeId.replace('fsq_', '');

      // When using backend proxy, don't send Authorization header (backend handles it)
      const headers: any = {
        'Accept': 'application/json',
      };
      
      if (!this.isUsingBackendProxy()) {
        // Direct API call or webpack proxy - send Authorization header
        headers['Authorization'] = `Bearer ${this.apiKey}`; // New auth format: Bearer <SERVICE_KEY>
        headers['X-Places-Api-Version'] = '2025-06-17'; // Version header: YYYY-MM-DD format (required)
      }

      const response = await axios.get(`${this.baseUrl}/${fsqId}`, {
        headers,
        params: {
          fields: 'name,location,categories,tel,website,rating,photos,hours,description',
        },
      });

      const place = response.data;
      return this.parseResults([place])[0] || null;

    } catch (error) {
      console.error('Error getting place details:', error);
      return null;
    }
  }

  /**
   * Check if API key is configured
   */
  isConfigured(): boolean {
    return this.apiKey !== 'fsq3YOUR_API_KEY_HERE' && this.apiKey.length > 10;
  }

  /**
   * Get configuration status
   */
  getConfigStatus(): { configured: boolean; message: string } {
    if (this.isConfigured()) {
      return {
        configured: true,
        message: '✅ Foursquare API is configured and ready to use'
      };
    }
    return {
      configured: false,
      message: '⚠️ Foursquare API key not configured. Update src/config/foursquareConfig.ts'
    };
  }

  /**
   * Get setup instructions
   */
  getSetupInstructions(): string {
    return `
🔑 Foursquare API Setup (FREE - 100,000 searches/month!)

1. Go to: https://foursquare.com/developers/signup
2. Create free account (no credit card required!)
3. Click "Create a New App"
4. Fill in app details and copy your API key
5. Update FOURSQUARE_API_KEY in src/config/foursquareConfig.ts

See FOURSQUARE_SETUP_GUIDE.md for detailed instructions.

No credit card required! 🎉
    `.trim();
  }
}

export const foursquarePlacesService = new FoursquarePlacesService();
export default foursquarePlacesService;




